import type { ComponentProps, ReactNode } from 'react';

import { CHAT_CARD_SHELL } from '@/components/ui/card-shell';
import { StatusAlert } from '@/components/ui/status-alert';
import { MESSAGE_TEXT_SCALE } from '@/components/ui/type-scale';
import { cn } from '@/lib/utils';

import type { LucideIcon } from 'lucide-react';

/** An in-chat step the guest takes: signing in, approving an action. Full width under a cap,
 *  so the card keeps its size as its state changes. */
export function ActionCard({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="action-card"
      {...props}
      style={MESSAGE_TEXT_SCALE}
      className={cn(CHAT_CARD_SHELL, 'my-3 flex w-full max-w-md flex-col p-4', className)}
    />
  );
}

export function ActionCardHeader({
  icon: Icon,
  tone = 'default',
  title,
  notice,
  announce = true,
  body,
  quiet,
}: {
  icon: LucideIcon;
  tone?: 'default' | 'destructive';
  title: string;
  /** What went wrong, read before the body that says what to do about it. */
  notice?: string;
  /** Off for a record, so its notice is not re-announced each time the thread loads. */
  announce?: boolean;
  body?: ReactNode;
  /** A record rather than an invitation: the body steps back. */
  quiet?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full',
            tone === 'destructive'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-primary/10 text-primary',
          )}
        >
          <Icon className="size-4" />
        </span>
        <h3 className="m-0 text-base leading-tight font-bold text-balance">{title}</h3>
      </div>
      {notice && (
        <div className="mt-2">
          <p
            role={announce ? 'alert' : undefined}
            className="text-sm whitespace-pre-line text-destructive"
          >
            {notice}
          </p>
        </div>
      )}
      {/* pre-line: tenant copy breaks its sentences onto lines of their own. */}
      {body && (
        <p className={cn('mt-2 text-sm whitespace-pre-line', quiet && 'text-muted-foreground')}>
          {body}
        </p>
      )}
    </div>
  );
}

/** `divided` sets the actions off from details above, as the reservation card does its footer. */
export function ActionCardActions({
  divided,
  stacked,
  className,
  ...props
}: ComponentProps<'div'> & { divided?: boolean; stacked?: boolean }) {
  return (
    <div
      {...props}
      className={cn(
        'mt-4 flex gap-2.5',
        stacked && 'flex-col',
        divided && 'border-t border-border/40 pt-4',
        className,
      )}
    />
  );
}

const OUTCOME_TONES = {
  /** Green whatever the tenant's primary, so success never reads as a brand-coloured notice. */
  ok: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
  /** `text-destructive` on its own tint is 3.99:1 in light mode, under AA. */
  error: 'bg-destructive/10 text-red-700 dark:text-red-300',
  live: 'bg-muted text-card-foreground',
  /** Only for something that no longer applies: declined, expired or cancelled. Half-strength
   *  muted: on the full tint the muted text falls to 4.39:1, under AA. */
  muted: 'bg-muted/50 text-muted-foreground',
} as const;

/** Replaces the actions once answered rather than disabling them, so nothing left in the
 *  thread looks as if it could still be pressed. */
export function ActionCardOutcome({
  icon,
  tone,
  children,
}: {
  icon: ComponentProps<typeof StatusAlert>['icon'];
  tone: keyof typeof OUTCOME_TONES;
  children: ReactNode;
}) {
  return (
    <div role="status" className="w-full">
      {/* The buttons' own height, so answering does not move anything below the card. */}
      <StatusAlert icon={icon} className={cn('min-h-10', OUTCOME_TONES[tone])}>
        {children}
      </StatusAlert>
    </div>
  );
}
